import { motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect } from "react";
import heroBg from "@/assets/hero-bg-movienight-illustrated.png";
import { useLowPower } from "@/hooks/useLowPower";

// Interactive cinematic background with mouse parallax and spotlight effect
export const CinemaBackground = () => {
  const lowPower = useLowPower();
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Interactive motion only for lighting/spotlight (background stays fixed to avoid edges)
  const lightingX = useTransform(mouseX, [0, 1], [-40, 40]);
  const lightingY = useTransform(mouseY, [0, 1], [-40, 40]);

  useEffect(() => {
    if (lowPower) return;
    const handleMouseMove = (e: MouseEvent) => {
      // Normalize mouse position to 0-1 range for parallax
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      
      mouseX.set(x);
      mouseY.set(y);

    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [mouseX, mouseY, lowPower]);

  // Touch / low-power devices get the same scene painted once: no infinite
  // scale tween, no cursor listener, no blend-mode compositing layer.
  if (lowPower) {
    return (
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(5,5,15,0.82), rgba(5,5,20,0.9)), url(${heroBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 12% 0%, hsla(var(--primary) / 0.32) 0, transparent 55%)," +
              "radial-gradient(circle at 88% 100%, hsla(var(--cta-primary) / 0.28) 0, transparent 55%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/40 to-transparent pointer-events-none" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base movie-night illustration (fixed, no parallax to prevent edges) */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5,5,15,0.82), rgba(5,5,20,0.9)), url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          scale: 1.2,
        }}
        animate={{
          scale: [1.2, 1.24, 1.22, 1.2],
        }}
        transition={{
          duration: 40,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Interactive premium lighting that follows cursor - extra large to prevent edges */}
      <motion.div
        className="absolute will-change-transform pointer-events-none"
        style={{
          inset: '-20%', // Extended beyond viewport
          background:
            "radial-gradient(circle at 10% 0%, hsla(var(--primary) / 0.5) 0, transparent 55%)," +
            "radial-gradient(circle at 90% 100%, hsla(var(--cta-primary) / 0.45) 0, transparent 55%)",
          x: lightingX,
          y: lightingY,
          mixBlendMode: 'screen',
        }}
        animate={{ opacity: [0.5, 0.9, 0.6, 0.5] }}
        transition={{ duration: 45, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Subtle vignette to keep content readable */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/40 to-transparent pointer-events-none" />
    </div>
  );
};
